import { useState } from "react";
import { ChatTeardropDots, SpinnerGap, Check, Bug, Lightbulb, ChatCircle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const TYPES = [
  { id: "bug", label: "Bug Report", icon: Bug, color: "text-red-400 bg-red-500/10" },
  { id: "feature_request", label: "Feature Request", icon: Lightbulb, color: "text-amber-400 bg-amber-500/10" },
  { id: "feedback", label: "General Feedback", icon: ChatCircle, color: "text-blue-400 bg-blue-500/10" },
];

const CATEGORIES = {
  bug: [
    { id: "generation_failed", label: "Generation Failed" },
    { id: "ui_broken", label: "UI Issue" },
    { id: "billing_issue", label: "Billing Issue" },
    { id: "login_auth", label: "Login / Auth" },
    { id: "performance", label: "Performance" },
    { id: "other", label: "Other" },
  ],
  feature_request: [
    { id: "new_content_type", label: "New Content Type" },
    { id: "new_platform", label: "New Platform" },
    { id: "integration", label: "Integration" },
    { id: "ui_improvement", label: "UI Improvement" },
    { id: "other", label: "Other" },
  ],
  feedback: [
    { id: "content_quality", label: "Content Quality" },
    { id: "ux_workflow", label: "UX / Workflow" },
    { id: "pricing", label: "Pricing" },
    { id: "general", label: "General" },
  ],
};

export default function FeedbackButton() {
  const { api, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user) return null;

  const reset = () => {
    setType("");
    setCategory("");
    setMessage("");
    setSent(false);
  };

  const submit = async () => {
    if (!type || !category || message.trim().length < 10) return;
    setSending(true);
    try {
      await api.post("/feedback", {
        type,
        category,
        message: message.trim(),
        page_url: window.location.pathname,
      });
      setSent(true);
      toast.success("Feedback submitted!");
      setTimeout(() => { setOpen(false); reset(); }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit feedback");
    }
    setSending(false);
  };

  const cats = CATEGORIES[type] || [];

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setOpen(true); reset(); }}
        className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-[#FF4400] hover:bg-[#FF5500] text-white shadow-lg shadow-[#FF4400]/20 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        data-testid="feedback-fab"
        title="Send Feedback"
      >
        <ChatTeardropDots size={22} weight="fill" />
      </button>

      {/* Modal */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="sm:max-w-md bg-[var(--bg-card)] border-[var(--border)]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl text-[var(--text-primary)]">
              Send Feedback
            </DialogTitle>
          </DialogHeader>

          {sent ? (
            <div className="flex flex-col items-center py-8 gap-3" data-testid="feedback-success">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Check size={24} className="text-emerald-400" weight="bold" />
              </div>
              <p className="text-sm text-[var(--text-primary)] font-medium">Thank you!</p>
              <p className="text-xs text-[var(--text-muted)]">Your feedback helps us improve Quibn.</p>
            </div>
          ) : (
            <div className="space-y-4 py-1">
              {/* Type selection */}
              <div>
                <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">What type?</label>
                <div className="grid grid-cols-3 gap-2">
                  {TYPES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { setType(t.id); setCategory(""); }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all text-center ${
                        type === t.id
                          ? "border-[#FF4400]/40 bg-[#FF4400]/5"
                          : "border-[var(--border)] hover:border-[var(--border-strong)]"
                      }`}
                      data-testid={`feedback-type-${t.id}`}
                    >
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${t.color}`}>
                        <t.icon size={18} />
                      </div>
                      <span className="text-[10px] font-medium text-[var(--text-primary)]">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              {type && (
                <div>
                  <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">Category</label>
                  <div className="flex flex-wrap gap-1.5">
                    {cats.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setCategory(c.id)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                          category === c.id
                            ? "border-[#FF4400]/40 bg-[#FF4400]/10 text-[#FF4400]"
                            : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                        }`}
                        data-testid={`feedback-cat-${c.id}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message */}
              {category && (
                <div>
                  <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">Details</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what happened or what you'd like to see..."
                    rows={4}
                    className="w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[#FF4400]/40 resize-none"
                    data-testid="feedback-message"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    {message.trim().length < 10 ? `${10 - message.trim().length} more characters needed` : "Ready to submit"}
                  </p>
                </div>
              )}
            </div>
          )}

          {!sent && (
            <DialogFooter>
              <Button
                onClick={submit}
                disabled={!type || !category || message.trim().length < 10 || sending}
                className="bg-[#FF4400] hover:bg-[#FF5500] text-white disabled:opacity-40"
                data-testid="feedback-submit"
              >
                {sending ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : null}
                Submit
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
